import i18n from "i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { detectLocale } from "./detect";
import { ensureLocaleFont } from "./fonts";
import { readStoredLocale, writeStoredLocale } from "./locale-store";
import { DEFAULT_LOCALE, isSupportedLocale, type Locale } from "./locales";

import enAccessCodes from "../../locales/en/accessCodes.json";
import enAccommodation from "../../locales/en/accommodation.json";
import enAccreditation from "../../locales/en/accreditation.json";
import enAffiliatedOrganizations from "../../locales/en/affiliatedOrganizations.json";
import enAirlines from "../../locales/en/airlines.json";
import enAssistant from "../../locales/en/assistant.json";
import enAuth from "../../locales/en/auth.json";
import enCommon from "../../locales/en/common.json";
import enCountries from "../../locales/en/countries.json";
import enDashboard from "../../locales/en/dashboard.json";
import enEAccreditation from "../../locales/en/eAccreditation.json";
import enEntries from "../../locales/en/entries.json";
import enEntriesStats from "../../locales/en/entriesStats.json";
import enEntryPoints from "../../locales/en/entryPoints.json";
import enFunctions from "../../locales/en/functions.json";
import enErrors from "../../locales/en/errors.json";
import enEvents from "../../locales/en/events.json";
import enFiles from "../../locales/en/files.json";
import enFnb from "../../locales/en/fnb.json";
import enHelpdesk from "../../locales/en/helpdesk.json";
import enHandoverLocations from "../../locales/en/handoverLocations.json";
import enIdScan from "../../locales/en/idScan.json";
import enParticipant from "../../locales/en/participant.json";
import enMerch from "../../locales/en/merch.json";
import enModules from "../../locales/en/modules.json";
import enNav from "../../locales/en/nav.json";
import enNocStages from "../../locales/en/nocStages.json";
import enChangelog from "../../locales/en/changelog.json";
import enReports from "../../locales/en/reports.json";
import enOrganizationTypes from "../../locales/en/organizationTypes.json";
import enQuotas from "../../locales/en/quotas.json";
import enRegistrationWindows from "../../locales/en/registrationWindows.json";
import enResponsibleOrganizations from "../../locales/en/responsibleOrganizations.json";
import enShortlist from "../../locales/en/shortlist.json";
import enSportClasses from "../../locales/en/sportClasses.json";
import enSports from "../../locales/en/sports.json";
import enSubAgencies from "../../locales/en/subAgencies.json";
import enTransport from "../../locales/en/transport.json";
import enTransportFleet from "../../locales/en/transportFleet.json";
import enTravel from "../../locales/en/travel.json";
import enTravelBoard from "../../locales/en/travelBoard.json";
import enTravelJourneys from "../../locales/en/travelJourneys.json";
import enUserGroups from "../../locales/en/userGroups.json";
import enUsers from "../../locales/en/users.json";
import enValidation from "../../locales/en/validation.json";
import enVenues from "../../locales/en/venues.json";

export { intlLocaleFor, LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from "./locales";

/**
 * English ships in the entry bundle: it is the fallback for any key a locale
 * is missing, and the source `types.d.ts` derives the typed t() keys from.
 * The other nine locales load on demand as their own Vite chunks.
 */
export const EN_RESOURCES = {
	accessCodes: enAccessCodes,
	accommodation: enAccommodation,
	accreditation: enAccreditation,
	affiliatedOrganizations: enAffiliatedOrganizations,
	airlines: enAirlines,
	assistant: enAssistant,
	auth: enAuth,
	common: enCommon,
	countries: enCountries,
	dashboard: enDashboard,
	eAccreditation: enEAccreditation,
	entries: enEntries,
	entriesStats: enEntriesStats,
	entryPoints: enEntryPoints,
	functions: enFunctions,
	errors: enErrors,
	events: enEvents,
	files: enFiles,
	fnb: enFnb,
	helpdesk: enHelpdesk,
	handoverLocations: enHandoverLocations,
	idScan: enIdScan,
	participant: enParticipant,
	merch: enMerch,
	modules: enModules,
	nav: enNav,
	nocStages: enNocStages,
	changelog: enChangelog,
	reports: enReports,
	organizationTypes: enOrganizationTypes,
	quotas: enQuotas,
	registrationWindows: enRegistrationWindows,
	responsibleOrganizations: enResponsibleOrganizations,
	shortlist: enShortlist,
	sportClasses: enSportClasses,
	sports: enSports,
	subAgencies: enSubAgencies,
	transport: enTransport,
	transportFleet: enTransportFleet,
	travel: enTravel,
	travelBoard: enTravelBoard,
	travelJourneys: enTravelJourneys,
	userGroups: enUserGroups,
	users: enUsers,
	validation: enValidation,
	venues: enVenues,
} as const;

const lazyLocales = import.meta.glob("../../locales/*/*.json");

const backend = resourcesToBackend((locale: string, namespace: string) => {
	const load = lazyLocales[`../../locales/${locale}/${namespace}.json`];
	if (!load) return Promise.reject(new Error(`No locale bundle ${locale}/${namespace}`));
	return load().then((m) => (m as { default: object }).default);
});

/** The active locale for anything outside React; components use useTranslation. */
export function currentLocale(): Locale {
	const lng = i18n.language;
	return isSupportedLocale(lng) ? lng : DEFAULT_LOCALE;
}

function applyLocaleSideEffects(locale: Locale): void {
	document.documentElement.lang = locale;
	ensureLocaleFont(locale);
}

/** Switch language: i18next, localStorage, <html lang> and the script's font. */
export async function setLocale(locale: Locale): Promise<void> {
	await i18n.changeLanguage(locale);
	writeStoredLocale(locale);
	applyLocaleSideEffects(locale);
}

/** Runs before the first render (main.tsx) so no frame paints untranslated. */
export async function initI18n(): Promise<void> {
	const stored = readStoredLocale();
	const initial: Locale =
		stored !== null && isSupportedLocale(stored) ? stored : detectLocale();

	await i18n
		.use(backend)
		.use(initReactI18next)
		.init({
			lng: initial,
			fallbackLng: DEFAULT_LOCALE,
			ns: Object.keys(EN_RESOURCES),
			defaultNS: "common",
			partialBundledLanguages: true,
			resources: { en: EN_RESOURCES },
			interpolation: { escapeValue: false }, // React escapes already
			react: { useSuspense: false }, // init awaits the bundles instead
			returnNull: false,
		});

	applyLocaleSideEffects(initial);
}

export { i18n };
